import React, { useState } from 'react';
import { Plus, Pencil, Stamp, Trash2, Tags } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import { licenseService } from '../../services/api';
import toast from 'react-hot-toast';
import FormModalLayout from '../../components/Common/FormModalLayout';
import AuthorizationTemplateModal from '../../components/License/AuthorizationTemplateModal';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { CHECKLIST_TIPOS, normalizeChecklistTipo } from '../../utils/checklistTipo';

const TiposLicenca = () => {
  const { t } = useTranslation('licenses');

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formNome, setFormNome] = useState('');
  const [checklist, setChecklist] = useState([]);
  const [templateTipo, setTemplateTipo] = useState(null);

  const { useList, useCreate, useUpdate, useDelete } = useCrud(
    'tipos-licenca',
    licenseService.tipoLicencaCrud
  );

  const { data: listData, isLoading, error } = useList();
  const items = Array.isArray(listData) ? listData : listData?.data ?? [];

  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const handleCreate = () => {
    setEditingItem(null);
    setFormNome('');
    setChecklist([]);
    setShowModal(true);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormNome(item.nome || '');
    setShowModal(true);
    try {
      const r = await licenseService.listTipoChecklist(item.id);
      setChecklist(Array.isArray(r.data) ? r.data : []);
    } catch {
      setChecklist([]);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este tipo de licença?')) {
      deleteMutation.mutate(id, {
        onSuccess: () => toast.success('Excluído com sucesso'),
        onError: (e) => toast.error(userFacingError(e, 'deleteError')),
      });
    }
  };

  const persistChecklist = async (tipoId) => {
    const current = await licenseService.listTipoChecklist(tipoId).then((r) => (Array.isArray(r.data) ? r.data : []));
    const keepIds = new Set(checklist.filter((row) => row.id && !row._deleted).map((row) => row.id));
    for (const row of current) {
      if (!keepIds.has(row.id)) {
        await licenseService.deleteTipoChecklistItem(row.id);
      }
    }
    for (const [index, row] of checklist.entries()) {
      if (row._deleted) continue;
      const payload = {
        descricao: (row.descricao || '').trim(),
        tipo_checklist: normalizeChecklistTipo(row.tipo_checklist),
        obrigatorio: Boolean(row.obrigatorio),
        ordem: index + 1,
      };
      if (!payload.descricao) continue;
      if (row.id) {
        await licenseService.updateTipoChecklistItem(row.id, payload);
      } else {
        await licenseService.createTipoChecklistItem(tipoId, payload);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formNome.trim()) {
      toast.error(t('nameRequired'));
      return;
    }
    if (editingItem) {
      updateMutation.mutate(
        { id: editingItem.id, data: { nome: formNome.trim() } },
        {
          onSuccess: async () => {
            try {
              await persistChecklist(editingItem.id);
              toast.success('Atualizado com sucesso');
              setShowModal(false);
              setEditingItem(null);
              setFormNome('');
              setChecklist([]);
            } catch (err) {
              toast.error(userFacingError(err, 'updateError'));
            }
          },
          onError: (err) => toast.error(userFacingError(err, 'updateError')),
        }
      );
    } else {
      createMutation.mutate(
        { nome: formNome.trim() },
        {
          onSuccess: async (created) => {
            try {
              const tipoId = created?.id;
              if (tipoId) await persistChecklist(tipoId);
              toast.success('Criado com sucesso');
              setShowModal(false);
              setFormNome('');
              setChecklist([]);
            } catch (err) {
              toast.error(userFacingError(err, 'createError'));
            }
          },
          onError: (err) => toast.error(userFacingError(err, 'createError')),
        }
      );
    }
  };

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("typesTitle")}
          description="Tipos de licença ambiental exibidos ao vincular processos e cadastros."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Gestão de licenças', to: '/licenses' },
            { label: 'Tipos' },
          ]}
        >
          <Can permission={P.LICENSE_TIPOS}>
            <button type="button" onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2 shadow-sm">
              <Plus className="h-4 w-4" />
              Novo tipo
            </button>
          </Can>
        </PageHeader>

      <div className="data-list-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={2} className="px-6 py-8 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.nome}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Can permission={P.LICENSE_TIPOS}>
                        <button
                          type="button"
                          onClick={() => setTemplateTipo(item)}
                          className="text-primary-600 hover:text-primary-800 mr-4"
                          title="Modelo de autorização (AP, ASV)"
                          aria-label={`Modelo de autorização de ${item.nome}`}
                        >
                          <Stamp className="w-4 h-4 inline" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="text-primary-600 hover:text-primary-800 mr-4"
                        >
                          <Pencil className="w-4 h-4 inline" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {templateTipo ? (
        <AuthorizationTemplateModal tipo={templateTipo} onClose={() => setTemplateTipo(null)} />
      ) : null}

      {showModal && (
        <FormModalLayout
          title={editingItem ? 'Editar tipo de licença' : 'Novo tipo de licença'}
          subtitle={t("typesSubtitle")}
          headerIcon={Tags}
          historyEntityType="license_types"
          historyEntityId={editingItem?.id}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
            setFormNome('');
          }}
          size="lg"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingItem(null);
                  setFormNome('');
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.LICENSE_TIPOS}>
                <button type="submit" form="tipo-licenca-form" className="btn btn-primary">
                  {editingItem ? 'Salvar' : 'Criar'}
                </button>
              </Can>
            </>
          }
        >
          <form id="tipo-licenca-form" onSubmit={handleSubmit} className="form-stack">
            <div className="form-group">
              <label htmlFor="tipo-lic-nome" className="form-label form-label-required">
                Nome
              </label>
              <input
                id="tipo-lic-nome"
                type="text"
                value={formNome}
                onChange={(e) => setFormNome(e.target.value)}
                className="form-input"
                placeholder="Ex.: licença ambiental de instalação"
              />
            </div>
            <div className="form-group">
              <div className="mb-2 flex items-center justify-between">
                <label className="form-label">Checklist do tipo</label>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() =>
                    setChecklist((rows) => [...rows, { descricao: '', tipo_checklist: 'basico', obrigatorio: true }])
                  }
                >
                  Adicionar item
                </button>
              </div>
              <p className="mb-3 text-xs text-gray-500">
                Itens obrigatórios passam a constar no cadastro da licença, com cumprimento e evidência.
              </p>
              <div className="space-y-3">
                {checklist.map((row, index) => (
                  <div key={row.id || `new-${index}`} className="rounded-lg border border-gray-200 p-3">
                    <textarea
                      className="form-input min-h-[64px]"
                      value={row.descricao}
                      onChange={(e) =>
                        setChecklist((rows) =>
                          rows.map((item, i) => (i === index ? { ...item, descricao: e.target.value } : item))
                        )
                      }
                      placeholder="Descrição do item"
                    />
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                      <label className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">{t('checklistType')}</span>
                        <select
                          className="form-input w-auto"
                          value={normalizeChecklistTipo(row.tipo_checklist)}
                          onChange={(e) =>
                            setChecklist((rows) =>
                              rows.map((item, i) =>
                                i === index ? { ...item, tipo_checklist: e.target.value } : item
                              )
                            )
                          }
                        >
                          {CHECKLIST_TIPOS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(row.obrigatorio)}
                          onChange={(e) =>
                            setChecklist((rows) =>
                              rows.map((item, i) =>
                                i === index ? { ...item, obrigatorio: e.target.checked } : item
                              )
                            )
                          }
                        />
                        Obrigatório
                      </label>
                      <button
                        type="button"
                        className="text-sm text-red-600"
                        onClick={() => setChecklist((rows) => rows.filter((_, i) => i !== index))}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default TiposLicenca;
